import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@chakra-ui/react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useWatchListEntries,
  useCreateWatchListEntry,
  useUpdateWatchListEntry,
  useDeleteWatchListEntry,
} from '../../../hooks/queries/useVisitorManagement';
import type {
  WatchListEntry,
  WatchListEntryWrite,
  FlagType,
  SeverityLevel,
} from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { StatusBadge, type Column, type StatusType } from '@themes/aegix';

const FLAG_TYPE_STATUS_MAP: Record<FlagType, StatusType> = {
  sex_offender: 'error' as StatusType,
  watch_list: 'warning' as StatusType,
  order_of_protection: 'error' as StatusType,
  custody_restriction: 'warning' as StatusType,
  custom: 'info' as StatusType,
};

interface WatchListRow {
  [key: string]: unknown;
  id: string;
  name: string;
  flag_type: FlagType;
  flag_type_display: string;
  severity: SeverityLevel;
  severity_display: string;
  reason: string;
  is_active: boolean;
}

export function useWatchListCrud() {
  const { t } = useTranslation();

  const columns = useMemo<Column<WatchListRow>[]>(() => [
    { key: 'name', label: t('visitors.col.name', 'Name'), sortable: true, width: '200px' },
    {
      key: 'flag_type_display',
      label: t('visitors.col.flagType', 'Flag Type'),
      sortable: true,
      width: '160px',
      render: (row) => (
        <StatusBadge
          status={FLAG_TYPE_STATUS_MAP[row.flag_type] ?? ('default' as StatusType)}
          label={row.flag_type_display}
        />
      ),
    },
    {
      key: 'severity_display',
      label: t('visitors.col.severity', 'Severity'),
      sortable: true,
      width: '100px',
    },
    {
      key: 'reason',
      label: t('visitors.col.reason', 'Reason'),
      render: (row) => (
        <Text fontSize="sm" lineClamp={1}>
          {row.reason}
        </Text>
      ),
    },
    {
      key: 'is_active',
      label: t('common.active', 'Active'),
      width: '80px',
      render: (row) => (
        <StatusBadge
          status={row.is_active ? ('active' as StatusType) : ('inactive' as StatusType)}
          label={row.is_active ? t('common.yes', 'Yes') : t('common.no', 'No')}
        />
      ),
    },
  ], [t]);
  const { data, isLoading, error } = useWatchListEntries({ is_active: true });
  const createMutation = useCreateWatchListEntry();
  const updateMutation = useUpdateWatchListEntry();
  const deleteMutation = useDeleteWatchListEntry();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<WatchListEntry | null>(null);

  const watchListEntries: WatchListEntry[] = data?.results ?? [];

  const rows: WatchListRow[] = useMemo(
    () =>
      watchListEntries.map((w: WatchListEntry) => ({
        ...w,
        id: w.public_id,
        name: `${w.first_name} ${w.last_name}`,
        flag_type: w.flag_type,
        flag_type_display: w.flag_type_display,
        severity: w.severity,
        severity_display: w.severity_display,
        reason: w.reason,
        is_active: w.is_active,
      })),
    [watchListEntries],
  );

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleRowClick = (row: WatchListRow) => {
    const item = watchListEntries.find(
      (w: WatchListEntry) => w.public_id === row.id,
    );
    if (item) {
      setEditItem(item);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = (
    values: WatchListEntryWrite,
    setError: UseFormSetError<WatchListEntryWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof WatchListEntryWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'documentation' && typeof value === 'string') return;
        if (Array.isArray(value)) {
          value.forEach((v) => formData.append(`${key}[]`, v));
        } else {
          formData.append(key, value instanceof Blob ? value : String(value));
        }
      }
    });

    if (editItem) {
      updateMutation.mutate(
        { publicId: editItem.public_id, data: formData },
        {
          onSuccess: () => {
            toaster.create({
              title: 'Watch list entry updated',
              type: 'success',
            });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          toaster.create({ title: 'Watch list entry added', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Watch list entry removed', type: 'success' });
        handleClose();
      },
      onError: (err) =>
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        }),
    });
  };

  const initialValues: WatchListEntryWrite = useMemo(
    () =>
      editItem
        ? {
            first_name: editItem.first_name,
            last_name: editItem.last_name,
            date_of_birth: editItem.date_of_birth ?? undefined,
            flag_type: editItem.flag_type,
            severity: editItem.severity,
            reason: editItem.reason,
            documentation: editItem.documentation,
            is_active: editItem.is_active,
            effective_from: editItem.effective_from ?? undefined,
            effective_until: editItem.effective_until ?? undefined,
          }
        : {
            first_name: '',
            last_name: '',
            flag_type: 'watch_list' as FlagType,
            severity: 'medium' as SeverityLevel,
            reason: '',
            is_active: true,
          },
    [editItem],
  );

  return {
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  };
}
